/**
 * The ?location_ids= filter the listings share: comma-separated, and ABSENT
 * when nothing is selected — an empty string would read as "no locations" and
 * an empty queue, where the server's default is "every location I may see".
 */
function locationIdsParam(locationIds) {
    const ids = (locationIds || []).filter(Boolean);
    return ids.length > 0 ? { location_ids: ids.join(',') } : {};
}
export class TimesheetApprovalService {
    constructor(apiClient) {
        this.apiClient = apiClient;
    }
    /**
     * Resolve the ACCOUNT pay period containing a date (409 APPROVALS_DISABLED
     * when approvals are off). Pay periods are one thing per account, so this
     * takes no location; the response names the timezone the days are counted
     * in. Where the person is known, prefer `getMine` — that resolves the
     * period in THEIR payroll timezone.
     */
    async getPeriod(accountId, date) {
        const params = {};
        if (date)
            params.date = date;
        return this.apiClient.get(`/accounts/${accountId}/timesheet-period`, params);
    }
    /**
     * Every undecided timesheet across recent ENDED periods, oldest first, one
     * row per PERSON. `locationIds` selects WHICH PEOPLE appear — it never
     * changes anybody's hours. Omit it for everyone the caller may see.
     */
    async listApprovable(accountId, locationIds) {
        return this.apiClient.get(`/accounts/${accountId}/timesheet-approvals/pending`, locationIdsParam(locationIds));
    }
    /**
     * Approval queue for the period containing the date
     * (work_sessions:approve), one row per PERSON. `locationIds` selects WHICH
     * PEOPLE appear — it never changes anybody's hours. Omit it for everyone
     * the caller may see.
     */
    async getQueue(accountId, date, locationIds) {
        const params = locationIdsParam(locationIds);
        if (date)
            params.date = date;
        return this.apiClient.get(`/accounts/${accountId}/timesheet-approvals`, params);
    }
    /**
     * Submit the caller's own timesheet for the period containing the date.
     * Deliberately sends NO location: the timesheet covers the person's whole
     * pay period at every location they worked, and the period is resolved in
     * their payroll timezone.
     */
    async submit(accountId, date) {
        return this.apiClient.post('/timesheet-approvals/submit', {
            account_id: accountId,
            ...(date ? { date } : {}),
        });
    }
    /** 409 PENDING_CORRECTIONS until every pending correction in the period is decided. */
    /**
     * The caller's own approval row (null when none) plus THEIR pay period for
     * a date — resolved in their payroll timezone, which is what a personal
     * timesheet must be drawn on. No location: one timesheet per person per
     * period, wherever the hours were worked.
     */
    async getMine(accountId, date) {
        const params = { account_id: accountId };
        if (date)
            params.date = date;
        return this.apiClient.get('/timesheet-approvals/mine', params);
    }
    /** Owner-only: return a SUBMITTED timesheet to pending to keep editing. */
    async withdraw(approvalId) {
        return this.apiClient.post(`/timesheet-approvals/${approvalId}/withdraw`);
    }
    async approve(approvalId, notes) {
        return this.apiClient.post(`/timesheet-approvals/${approvalId}/approve`, notes ? { notes } : {});
    }
    async reject(approvalId, notes) {
        return this.apiClient.post(`/timesheet-approvals/${approvalId}/reject`, { notes });
    }
    async reopen(approvalId, notes) {
        return this.apiClient.post(`/timesheet-approvals/${approvalId}/reopen`, { notes });
    }
}
